import { useEffect, useMemo, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api";
import { IconChevronL, IconChevronR } from "@/components/admin/Icons";

type Slot = {
  id: number;
  data: string;       // YYYY-MM-DD
  dia_semana: string;
  inicio: string;     // HH:MM:SS
  fim: string;
  capacidade: number;
  ativo: 0 | 1;
  observacoes: string | null;
  usados: number;
};

const DOW_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MONTH_NAMES = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

function pad(n: number, w = 2): string { return String(n).padStart(w, "0"); }
function ymd(d: Date): string { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function ym(d: Date): string  { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`; }
function fmtTime(t: string): string {
  const [hh, mm] = t.split(":");
  if (!hh) return t;
  const h = Number(hh);
  return mm && mm !== "00" ? `${h}h${mm}` : `${h}h`;
}

function buildMonthGrid(year: number, month: number): Date[] {
  // mês 0-indexado
  const first = new Date(year, month, 1);
  const firstDow = first.getDay();
  const start = new Date(year, month, 1 - firstDow);
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

type DayModalState = {
  date: string;
  slots: Slot[];
};

export default function AdminAgenda() {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dayModal, setDayModal] = useState<DayModalState | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const loadMonth = (d: Date) => {
    setLoading(true);
    apiFetch<{ slots: Slot[] }>(`/admin/slots?month=${ym(d)}`, { auth: "admin" })
      .then((res) => setSlots(res.slots))
      .catch(() => setError("Falha ao carregar a agenda."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadMonth(cursor); }, [cursor]);

  const slotsByDay = useMemo(() => {
    const map: Record<string, Slot[]> = {};
    for (const s of slots) {
      (map[s.data] ??= []).push(s);
    }
    return map;
  }, [slots]);

  const grid = buildMonthGrid(cursor.getFullYear(), cursor.getMonth());
  const todayStr = ymd(today);

  const toast = (msg: string, err = false) => {
    setToastMsg(`${err ? "err:" : "ok:"}${msg}`);
    setTimeout(() => setToastMsg(null), 2500);
  };

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));

  const openDay = (date: string) => {
    setDayModal({ date, slots: slotsByDay[date] ?? [] });
  };
  const closeDay = () => setDayModal(null);

  const refresh = () => loadMonth(cursor);

  const replicateWeek = async () => {
    if (!confirm("Replicar todos os horários da semana atual (Dom→Sáb) para o restante do mês?")) return;
    try {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      const weekSlots = slots.filter((s) => {
        const sd = new Date(`${s.data}T00:00:00`);
        return sd >= weekStart && sd <= weekEnd;
      });
      if (weekSlots.length === 0) {
        toast("Sem horários nesta semana para replicar", true);
        return;
      }
      const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
      const created: Array<{ data: string; inicio: string; fim: string; capacidade: number; ativo: boolean }> = [];
      const cursorDay = new Date(weekEnd);
      cursorDay.setDate(weekEnd.getDate() + 1);
      while (cursorDay <= monthEnd) {
        const dow = cursorDay.getDay();
        const same = weekSlots.filter((s) => new Date(`${s.data}T00:00:00`).getDay() === dow);
        for (const s of same) {
          created.push({
            data: ymd(cursorDay),
            inicio: s.inicio,
            fim: s.fim,
            capacidade: s.capacidade,
            ativo: s.ativo === 1,
          });
        }
        cursorDay.setDate(cursorDay.getDate() + 1);
      }
      if (created.length === 0) {
        toast("Não há dias restantes no mês", true);
        return;
      }
      const res = await apiFetch<{ created: number }>("/admin/slots/bulk", {
        method: "POST",
        body: { slots: created },
        auth: "admin",
      });
      toast(`${res.created} horários criados`);
      refresh();
    } catch {
      toast("Erro ao replicar", true);
    }
  };

  return (
    <>
      <div className="adm-topbar">
        <div>
          <h1 className="adm-title">Agenda</h1>
          <p className="adm-sub">Gerencie os horários disponíveis para a Avaliação Diagnóstica.</p>
        </div>
        <div className="adm-topactions">
          <button type="button" className="btn btn-ghost" onClick={goToday} style={{ padding: "10px 16px", fontSize: 14 }}>
            Hoje
          </button>
          <button type="button" className="btn btn-primary" onClick={replicateWeek} style={{ padding: "10px 18px", fontSize: 14 }}>
            Replicar semana no mês
          </button>
        </div>
      </div>

      {error && <div className="adm-login-error">{error}</div>}

      <div className="adm-cal">
        <div className="adm-cal-head">
          <h2 className="adm-cal-title">
            {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
          </h2>
          <div className="adm-cal-nav">
            <button type="button" className="adm-btn-icon" onClick={goPrev} title="Mês anterior">
              <IconChevronL />
            </button>
            <button type="button" className="adm-btn-icon" onClick={goNext} title="Próximo mês">
              <IconChevronR />
            </button>
          </div>
        </div>

        <div className="adm-cal-grid">
          {DOW_SHORT.map((d) => (
            <div key={d} className="adm-cal-dow">{d}</div>
          ))}
          {grid.map((d, i) => {
            const inMonth = d.getMonth() === cursor.getMonth();
            const dateStr = ymd(d);
            const list = slotsByDay[dateStr] ?? [];
            return (
              <button
                type="button"
                key={i}
                className={`adm-cal-day ${inMonth ? "" : "muted"} ${dateStr === todayStr ? "today" : ""}`}
                onClick={() => openDay(dateStr)}
              >
                <span className="adm-cal-daynum">{d.getDate()}</span>
                {list.map((s) => (
                  <span
                    key={s.id}
                    className={`adm-cal-pill ${s.ativo === 1 ? (s.usados > 0 ? "booked" : "") : "inactive"}`}
                    title={s.observacoes ?? undefined}
                  >
                    <span>{fmtTime(s.inicio)}</span>
                    {s.usados > 0 && <span>{s.usados}/{s.capacidade}</span>}
                  </span>
                ))}
              </button>
            );
          })}
        </div>

        {loading && <p style={{ color: "var(--ink-3)", marginTop: 14, fontSize: 14 }}>Carregando…</p>}
      </div>

      {dayModal && (
        <DayEditor
          state={dayModal}
          onClose={closeDay}
          onChanged={() => { closeDay(); refresh(); toast("Agenda atualizada"); }}
          onError={(msg) => toast(msg, true)}
        />
      )}

      {toastMsg && (
        <div className={`adm-toast ${toastMsg.startsWith("err:") ? "err" : "ok"}`}>
          {toastMsg.slice(3)}
        </div>
      )}
    </>
  );
}

// -------------------------------------------------------------
// Day editor modal
// -------------------------------------------------------------

const QUICK_TIMES: Array<[string, string]> = [
  ["07:30", "08:30"],
  ["09:00", "10:00"],
  ["10:30", "11:30"],
  ["14:00", "15:00"],
  ["17:00", "18:00"],
  ["19:30", "20:30"],
];

type EditorProps = {
  state: DayModalState;
  onClose: () => void;
  onChanged: () => void;
  onError: (msg: string) => void;
};

function DayEditor({ state, onClose, onChanged, onError }: EditorProps) {
  const [newInicio, setNewInicio] = useState("");
  const [newFim, setNewFim] = useState("");
  const [capacidade, setCapacidade] = useState(1);
  const [working, setWorking] = useState(false);
  const [editing, setEditing] = useState<Slot[]>(state.slots.map((s) => ({ ...s })));

  const addSlot = async (inicio: string, fim: string) => {
    if (!inicio || !fim) return;
    setWorking(true);
    try {
      await apiFetch("/admin/slots", {
        method: "POST",
        auth: "admin",
        body: {
          data: state.date,
          inicio,
          fim,
          capacidade,
          ativo: true,
        },
      });
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) onError("Esse horário já existe nesse dia");
      else onError("Erro ao adicionar horário");
      setWorking(false);
    }
  };

  const toggleActive = async (s: Slot) => {
    setWorking(true);
    try {
      await apiFetch(`/admin/slots/${s.id}`, {
        method: "PATCH",
        auth: "admin",
        body: { ativo: s.ativo === 1 ? false : true },
      });
      onChanged();
    } catch {
      onError("Erro ao alterar slot");
      setWorking(false);
    }
  };

  const deleteSlot = async (s: Slot) => {
    if (!confirm(`Remover ${fmtTime(s.inicio)} – ${fmtTime(s.fim)}?`)) return;
    setWorking(true);
    try {
      await apiFetch(`/admin/slots/${s.id}`, {
        method: "DELETE",
        auth: "admin",
      });
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) onError("Slot já tem agendamento — cancele o booking primeiro");
      else onError("Erro ao remover");
      setWorking(false);
    }
  };

  const updateCapacity = async (s: Slot, cap: number) => {
    setWorking(true);
    try {
      await apiFetch(`/admin/slots/${s.id}`, {
        method: "PATCH",
        auth: "admin",
        body: { capacidade: cap },
      });
      setEditing((arr) => arr.map((x) => (x.id === s.id ? { ...x, capacidade: cap } : x)));
    } catch {
      onError("Erro ao alterar capacidade");
    } finally {
      setWorking(false);
    }
  };

  const [y, m, d] = state.date.split("-");
  const label = y && m && d ? `${d}/${m}/${y}` : state.date;

  return (
    <div className="adm-modal-back" onClick={onClose}>
      <div className="adm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <h3>Horários · {label}</h3>

        <div className="adm-modal-body">
          {editing.length === 0 ? (
            <p className="adm-answer-empty">Nenhum horário cadastrado para esta data.</p>
          ) : (
            editing.map((s) => (
              <div
                key={s.id}
                className="adm-slot-row"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: 12,
                  border: "1px solid var(--line-soft)",
                  borderRadius: 12,
                  opacity: s.ativo ? 1 : 0.55,
                }}
              >
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: 15 }}>{fmtTime(s.inicio)} – {fmtTime(s.fim)}</strong>
                  <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                    {s.usados} / {s.capacidade} vagas usadas {s.ativo === 0 && "· inativo"}
                  </div>
                </div>
                <select
                  className="adm-select"
                  value={s.capacidade}
                  onChange={(e) => updateCapacity(s, Number(e.target.value))}
                  disabled={working}
                  style={{ padding: "6px 10px", fontSize: 13 }}
                >
                  {[1,2,3,4,5,6].map((n) => (
                    <option key={n} value={n}>{n} vaga{n > 1 ? "s" : ""}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="adm-status-btn"
                  onClick={() => toggleActive(s)}
                  disabled={working}
                >
                  {s.ativo ? "Desativar" : "Ativar"}
                </button>
                <button
                  type="button"
                  className="adm-status-btn"
                  onClick={() => deleteSlot(s)}
                  disabled={working || s.usados > 0}
                  title={s.usados > 0 ? "Cancele o booking antes" : undefined}
                  style={{ color: "oklch(0.55 0.18 25)", borderColor: "oklch(0.6 0.18 25 / .35)" }}
                >
                  Excluir
                </button>
              </div>
            ))
          )}

          <div style={{ borderTop: "1px dashed var(--line)", paddingTop: 14 }}>
            <strong style={{ fontSize: 13, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".08em" }}>
              Adicionar
            </strong>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
              {QUICK_TIMES.map(([ini, fim]) => (
                <button
                  key={`${ini}-${fim}`}
                  type="button"
                  className="adm-status-btn"
                  onClick={() => addSlot(ini, fim)}
                  disabled={working}
                >
                  {fmtTime(ini)}–{fmtTime(fim)}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <input
                type="time"
                className="adm-input"
                value={newInicio}
                onChange={(e) => setNewInicio(e.target.value)}
                placeholder="Início"
                style={{ flex: 1 }}
              />
              <input
                type="time"
                className="adm-input"
                value={newFim}
                onChange={(e) => setNewFim(e.target.value)}
                placeholder="Fim"
                style={{ flex: 1 }}
              />
              <select
                className="adm-select"
                value={capacidade}
                onChange={(e) => setCapacidade(Number(e.target.value))}
              >
                {[1,2,3,4,5,6].map((n) => (
                  <option key={n} value={n}>{n} vaga{n > 1 ? "s" : ""}</option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => addSlot(newInicio, newFim)}
                disabled={working || !newInicio || !newFim}
                style={{ padding: "10px 16px", fontSize: 14 }}
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>

        <div className="adm-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} style={{ padding: "10px 20px", fontSize: 14 }}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
